/**
 * ET-109 — engagement-stall detection for the Patiently CTA modal. Three
 * independent signals, any one of which fires the modal:
 *
 *  - `inactivity`   — no meaningful interaction for `inactivityMs` (default
 *    20s per ticket AC #1). "Meaningful" excludes bare mouse movement —
 *    pointerdown/keydown/scroll/touchstart only, so idle mouse jitter over
 *    the page doesn't keep resetting the timer.
 *  - `scroll_bottom` — the user reaches the bottom of the page (AC #2).
 *  - `exit_intent`   — the cursor leaves the top of the viewport, desktop
 *    only (AC #3; mobile has no cursor to leave with, and Owen's 2026-07-08
 *    comment explicitly scopes exit-intent to desktop).
 *
 * Desktop detection uses `matchMedia("(pointer: fine)")` rather than the
 * shared `classifyDevice()` UA classifier (`~/segment/utils`): exit-intent is
 * about pointer physics (does a mouse exist to leave the viewport with), not
 * analytics device-bucketing, and `classifyDevice` intentionally preserves
 * SFRA quirks (e.g. Android tablets without "Mobile" in the UA classify as
 * "desktop") that would wrongly arm a mouseout listener on a touch-only
 * device. `trackPageEvent` still decorates every fired analytics event with
 * `classifyDevice`'s `device` value independently of this check.
 *
 * Only the first trigger to fire wins (`firedRef`) — the caller is expected
 * to flip `enabled` to `false` in response to `onTrigger`, which runs this
 * effect's cleanup and tears every listener down.
 */
import { useEffect, useRef } from "react";

export type EngagementStallTriggerType =
  | "inactivity"
  | "scroll_bottom"
  | "exit_intent";

export interface UseEngagementStallTriggersOptions {
  /** Arm/disarm all listeners. `false` tears everything down via cleanup. */
  enabled: boolean;
  /** Inactivity duration before the `inactivity` trigger fires. */
  inactivityMs?: number;
  onTrigger: (trigger: EngagementStallTriggerType) => void;
}

const DEFAULT_INACTIVITY_MS = 20_000;
const SCROLL_BOTTOM_THRESHOLD_PX = 50;
const INTERACTION_EVENTS = [
  "pointerdown",
  "keydown",
  "scroll",
  "touchstart",
] as const;

function isFinePointerDevice(): boolean {
  if (
    typeof window === "undefined" ||
    typeof window.matchMedia !== "function"
  ) {
    return false;
  }
  return window.matchMedia("(pointer: fine)").matches;
}

function isAtScrollBottom(): boolean {
  const doc = document.documentElement;
  return (
    window.innerHeight + window.scrollY >=
    doc.scrollHeight - SCROLL_BOTTOM_THRESHOLD_PX
  );
}

export function useEngagementStallTriggers({
  enabled,
  inactivityMs = DEFAULT_INACTIVITY_MS,
  onTrigger,
}: UseEngagementStallTriggersOptions): void {
  const onTriggerRef = useRef(onTrigger);
  onTriggerRef.current = onTrigger;

  useEffect(() => {
    if (!enabled || typeof window === "undefined") return;

    const firedRef = { current: false };
    const fire = (trigger: EngagementStallTriggerType) => {
      if (firedRef.current) return;
      firedRef.current = true;
      onTriggerRef.current(trigger);
    };

    // A backgrounded tab must not "stall": firing there sends phantom
    // triggered/viewed events and burns the one-shot session suppression on a
    // modal nobody saw. If the timer lapses while hidden, re-arm instead of
    // firing; when the tab becomes visible again, restart the countdown so
    // time spent hidden never counts as visible inactivity.
    const onInactivityElapsed = () => {
      if (document.visibilityState === "hidden") {
        resetInactivityTimer();
        return;
      }
      fire("inactivity");
    };
    let inactivityTimer: number;
    const resetInactivityTimer = () => {
      window.clearTimeout(inactivityTimer);
      inactivityTimer = window.setTimeout(onInactivityElapsed, inactivityMs);
    };
    resetInactivityTimer();
    for (const eventName of INTERACTION_EVENTS) {
      window.addEventListener(eventName, resetInactivityTimer, {
        passive: true,
      });
    }
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") resetInactivityTimer();
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);

    const handleScroll = () => {
      if (isAtScrollBottom()) fire("scroll_bottom");
    };
    window.addEventListener("scroll", handleScroll, { passive: true });

    const desktop = isFinePointerDevice();
    const handleMouseOut = (event: MouseEvent) => {
      if (event.relatedTarget === null && event.clientY <= 0) {
        fire("exit_intent");
      }
    };
    if (desktop) {
      document.addEventListener("mouseout", handleMouseOut);
    }

    return () => {
      window.clearTimeout(inactivityTimer);
      for (const eventName of INTERACTION_EVENTS) {
        window.removeEventListener(eventName, resetInactivityTimer);
      }
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("scroll", handleScroll);
      if (desktop) {
        document.removeEventListener("mouseout", handleMouseOut);
      }
    };
  }, [enabled, inactivityMs]);
}
